/**
 * AccessoAziendaCard — il super utente blocca o sblocca l'accesso di un'azienda intera (24/10/2026).
 *
 * Simone, su Energ.it: «una funzionalità nella console che permetta di bloccare l'accesso a una determinata azienda»,
 * col resoconto PRIMA: quanti e quali account si chiudono, l'ultima volta che l'azienda ha usato il sistema. Il bottone
 * apre la finestra, la finestra chiede l'anteprima al server e la mostra così com'è; la conferma manda l'impronta di
 * QUELLA anteprima, e il server si rifiuta se nel frattempo è cambiato qualcosa. La logica è tutta sul server
 * (`functions/src/services/aziende/accessoAzienda.ts`), la stessa che usa la skill /probusiness.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, TextField,
  Typography,
} from '@mui/material';
import { Block, LockOpen } from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { creaContestoLocale } from '@gestionale/core/i18n/lingue';
import { formatData } from '@gestionale/core/i18n/formattazione';
import { functions } from '../../firebase';
import { tokens } from '../../theme';

type Azione = 'blocca' | 'sblocca';

interface PersonaAccesso {
  uid: string;
  nome: string;
  email: string;
  ruoli: string[];
  ultima_attivita: string | null;
  ultimo_ingresso: string | null;
  motivo?: string;
  chiusa_da?: string[];
}

/** La forma di `AnteprimaAccesso` del server (`services/aziende/accessoAzienda.ts`). */
interface AnteprimaAccesso {
  azione: Azione;
  azienda: {
    id: string; nome: string; piano: string | null; stato: string; esente: boolean; motivo_esenzione: string | null;
  };
  pagamenti: {
    abbonamento_id: string | null; stato_abbonamento: string | null; abbonamento_vivo: boolean;
    ha_mai_pagato: boolean; ultimo_pagamento: string | null;
  };
  da_cambiare: PersonaAccesso[];
  restano_chiusi: PersonaAccesso[];
  gia_chiusi: PersonaAccesso[];
  esclusi: PersonaAccesso[];
  ultima_attivita_azienda: string | null;
  impedimenti: string[];
  impronta: string;
}

interface EsitoAccesso {
  cambiati: string[];
  restano_chiusi: string[];
  errori: { uid: string; errore: string }[];
}

/** Il blocco come sta sull'azienda (`tenants/{id}.blocco_accesso`). */
interface BloccoSullAzienda {
  dal?: { seconds: number } | { toDate: () => Date } | string | null;
  motivo?: string;
  eseguito_da?: string;
}

interface Props {
  tenant: { id: string; nome_azienda?: string; blocco_accesso?: BloccoSullAzienda | null };
  onChange: () => void;
}

function isoDi(valore: BloccoSullAzienda['dal']): string | null {
  if (!valore) return null;
  if (typeof valore === 'string') return valore;
  if ('toDate' in valore) return valore.toDate().toISOString();
  return new Date(valore.seconds * 1000).toISOString();
}

export const AccessoAziendaCard: React.FC<Props> = ({ tenant, onChange }) => {
  const { t, i18n } = useTranslation('aziende');
  const ctx = useMemo(
    () => creaContestoLocale({ lingua: i18n.language, paese: 'IT', valuta: 'EUR', fusoOrario: 'Europe/Rome' }),
    [i18n.language],
  );
  const giorno = useCallback((iso: string | null) => (iso ? formatData(iso, ctx, 'media') : t('accesso.mai')), [ctx, t]);

  const [azione, setAzione] = useState<Azione | null>(null);
  const [anteprima, setAnteprima] = useState<AnteprimaAccesso | null>(null);
  const [motivo, setMotivo] = useState('');
  const [lavoro, setLavoro] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [esito, setEsito] = useState<string | null>(null);

  const blocco = tenant.blocco_accesso ?? null;
  const nome = tenant.nome_azienda ?? tenant.id;

  const apri = async (quale: Azione) => {
    setAzione(quale);
    setAnteprima(null);
    setMotivo('');
    setErrore(null);
    setLavoro(true);
    try {
      const fn = httpsCallable<object, AnteprimaAccesso>(functions, 'consoleAccessoAzienda');
      const r = await fn({ azione: quale === 'blocca' ? 'anteprima_blocco' : 'anteprima_sblocco', tenant_id: tenant.id });
      setAnteprima(r.data);
    } catch (e) {
      setErrore(t('accesso.errore', { messaggio: e instanceof Error ? e.message : String(e) }));
    } finally {
      setLavoro(false);
    }
  };

  const chiudi = () => {
    if (lavoro) return;
    setAzione(null);
    setAnteprima(null);
  };

  const conferma = async () => {
    if (!azione || !anteprima) return;
    setLavoro(true);
    setErrore(null);
    try {
      const fn = httpsCallable<object, EsitoAccesso>(functions, 'consoleAccessoAzienda');
      const r = await fn({ azione, tenant_id: tenant.id, impronta: anteprima.impronta, motivo: motivo.trim() });
      const righe = [
        t(azione === 'blocca' ? 'accesso.esito_blocco' : 'accesso.esito_sblocco', { count: r.data.cambiati.length }),
        r.data.restano_chiusi.length > 0 ? t('accesso.esito_restano', { count: r.data.restano_chiusi.length }) : '',
        r.data.errori.length > 0 ? t('accesso.esito_errori', { count: r.data.errori.length }) : '',
      ];
      setEsito(righe.filter(Boolean).join(' '));
      setAzione(null);
      setAnteprima(null);
      onChange();
    } catch (e) {
      setErrore(t('accesso.errore', { messaggio: e instanceof Error ? e.message : String(e) }));
    } finally {
      setLavoro(false);
    }
  };

  const puoConfermare = !!anteprima && anteprima.impedimenti.length === 0 && !lavoro
    && (azione === 'sblocca' || motivo.trim().length > 0);

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
      <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: '1.15rem', mb: 1 }}>
        {t('accesso.titolo')}
      </Typography>
      {esito && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEsito(null)}>{esito}</Alert>}
      {blocco ? (
        <Stack spacing={1.5}>
          <Alert severity="error" icon={<Block />}>
            {t('accesso.bloccata_dal', { quando: giorno(isoDi(blocco.dal)) })}
            {blocco.eseguito_da && ` (${t(blocco.eseguito_da === 'skill_probusiness' ? 'accesso.bloccata_da_skill' : 'accesso.bloccata_da_console')})`}
            {blocco.motivo && <Box component="span" sx={{ display: 'block' }}>{t('accesso.bloccata_motivo', { motivo: blocco.motivo })}</Box>}
          </Alert>
          <Box>
            <Button variant="outlined" startIcon={<LockOpen />} onClick={() => apri('sblocca')} sx={{ textTransform: 'none' }}>
              {t('accesso.bottone_sblocca')}
            </Button>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Typography variant="body2" sx={{ color: tokens.color.ash }}>{t('accesso.spiegazione_libera')}</Typography>
          <Box>
            <Button variant="outlined" color="error" startIcon={<Block />} onClick={() => apri('blocca')} sx={{ textTransform: 'none' }}>
              {t('accesso.bottone_blocca')}
            </Button>
          </Box>
        </Stack>
      )}

      <Dialog open={!!azione} onClose={chiudi} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600 }}>
          {t(azione === 'sblocca' ? 'accesso.titolo_sblocco' : 'accesso.titolo_blocco', { nome })}
        </DialogTitle>
        <DialogContent dividers>
          {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}
          {!anteprima && lavoro && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">{t('accesso.caricamento')}</Typography>
            </Stack>
          )}
          {anteprima && azione && (
            <Resoconto anteprima={anteprima} azione={azione} giorno={giorno} />
          )}
          {anteprima && anteprima.impedimenti.length === 0 && (
            <TextField
              label={t('accesso.motivo')}
              helperText={t(azione === 'blocca' ? 'accesso.motivo_aiuto_blocco' : 'accesso.motivo_aiuto_sblocco')}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              required={azione === 'blocca'}
              fullWidth
              size="small"
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={chiudi} disabled={lavoro} color="inherit">{t('accesso.annulla')}</Button>
          <Button
            variant="contained"
            color={azione === 'blocca' ? 'error' : 'primary'}
            onClick={conferma}
            disabled={!puoConfermare}
            startIcon={lavoro && anteprima ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {azione === 'blocca'
              ? t('accesso.conferma_blocco', { count: anteprima?.da_cambiare.length ?? 0 })
              : t('accesso.conferma_sblocco')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

/** Il resoconto dell'anteprima, così come lo manda il server. */
const Resoconto: React.FC<{ anteprima: AnteprimaAccesso; azione: Azione; giorno: (iso: string | null) => string }> = ({
  anteprima, azione, giorno,
}) => {
  const { t } = useTranslation('aziende');
  const { azienda, pagamenti } = anteprima;
  const riga = (p: PersonaAccesso, extra?: string) => (
    <Box key={p.uid} sx={{ py: 0.75, borderBottom: `1px solid ${tokens.color.lineSoft}` }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {p.nome}
        <Box component="span" sx={{ fontWeight: 400, color: tokens.color.ash }}>
          {' · '}{p.ruoli.length > 0 ? p.ruoli.join(', ') : t('accesso.senza_ruolo')}{p.email ? ` · ${p.email}` : ''}
        </Box>
      </Typography>
      <Typography variant="caption" sx={{ color: tokens.color.ash, display: 'block' }}>
        {t('accesso.persona_attivita', { attivita: giorno(p.ultima_attivita), ingresso: giorno(p.ultimo_ingresso) })}
        {extra ? ` · ${extra}` : ''}
      </Typography>
    </Box>
  );
  const pagato = pagamenti.ultimo_pagamento
    ? t('accesso.ultimo_pagamento', { quando: giorno(pagamenti.ultimo_pagamento) })
    : t(pagamenti.ha_mai_pagato ? 'accesso.pagato_in_passato' : 'accesso.mai_pagato');
  const chiusoDa = (p: PersonaAccesso) => (p.chiusa_da && p.chiusa_da.length > 0
    ? t('accesso.chiuso_da', { chi: p.chiusa_da.join(', ') })
    : t('accesso.chiuso_prima_del_registro'));

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="body2">
          {azienda.piano && t('accesso.piano', { piano: azienda.piano })}
          {azienda.esente && ` · ${azienda.motivo_esenzione ? t('accesso.esente', { motivo: azienda.motivo_esenzione }) : t('accesso.esente_senza_motivo')}`}
        </Typography>
        <Typography variant="body2">
          {pagamenti.abbonamento_id
            ? t('accesso.abbonamento', { id: pagamenti.abbonamento_id, stato: pagamenti.stato_abbonamento ?? '?' })
            : t('accesso.nessun_abbonamento')}
          {` · ${pagato}`}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
          {t('accesso.ultima_attivita_azienda', { quando: giorno(anteprima.ultima_attivita_azienda) })}
        </Typography>
      </Box>

      {anteprima.impedimenti.map((i) => (
        <Alert key={i} severity="warning">{t(`accesso.impedimenti.${i}` as 'accesso.impedimenti.gia_bloccata', { defaultValue: i })}</Alert>
      ))}

      <Box>
        <Typography variant="subtitle2">
          {t(azione === 'blocca' ? 'accesso.si_chiudono' : 'accesso.si_riaprono', { count: anteprima.da_cambiare.length })}
        </Typography>
        {anteprima.da_cambiare.length === 0
          ? <Typography variant="body2" sx={{ color: tokens.color.mist }}>{t('accesso.nessuno')}</Typography>
          : anteprima.da_cambiare.map((p) => riga(p))}
      </Box>
      {anteprima.restano_chiusi.length > 0 && (
        <Box>
          <Typography variant="subtitle2">{t('accesso.restano_chiusi')}</Typography>
          {anteprima.restano_chiusi.map((p) => riga(p, chiusoDa(p)))}
        </Box>
      )}
      {anteprima.gia_chiusi.length > 0 && (
        <Box>
          <Typography variant="subtitle2">{t('accesso.gia_chiusi')}</Typography>
          {anteprima.gia_chiusi.map((p) => riga(p, chiusoDa(p)))}
        </Box>
      )}
      {anteprima.esclusi.length > 0 && (
        <Box>
          <Typography variant="subtitle2">{t('accesso.non_si_toccano')}</Typography>
          {anteprima.esclusi.map((p) => riga(p, t(`accesso.motivi.${p.motivo}` as 'accesso.motivi.super_utente', { defaultValue: p.motivo ?? '' })))}
        </Box>
      )}
      <Alert severity={azione === 'blocca' ? 'warning' : 'info'}>
        {t(azione === 'blocca' ? 'accesso.cosa_succede_blocco' : 'accesso.cosa_succede_sblocco')}
      </Alert>
    </Stack>
  );
};
